/**
 * Channels React Query Hooks
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ChannelsService, type ChannelListParams } from '../api/services/channelsService';
import type { Channel } from '../api/types';

export const channelKeys = {
  all: ['channels'] as const,
  list: (params?: ChannelListParams) => [...channelKeys.all, 'list', params] as const,
  detail: (id: string) => [...channelKeys.all, id] as const,
  status: (id: string) => [...channelKeys.all, id, 'status'] as const,
  mixes: (id: string) => [...channelKeys.all, id, 'mixes'] as const,
};

export function useChannels(params?: ChannelListParams) {
  return useQuery({
    queryKey: channelKeys.list(params),
    queryFn: () => ChannelsService.list(params),
  });
}

export function useChannel(id: string) {
  return useQuery({
    queryKey: channelKeys.detail(id),
    queryFn: () => ChannelsService.get(id),
    enabled: !!id,
  });
}

export function useChannelMixes(id: string) {
  return useQuery({
    queryKey: channelKeys.mixes(id),
    queryFn: () => ChannelsService.listMixes(id),
    enabled: !!id,
  });
}

export function useCreateChannel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<Channel>) => ChannelsService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: channelKeys.all });
    },
  });
}

export function useUpdateChannel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Channel> }) =>
      ChannelsService.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: channelKeys.all });
      queryClient.invalidateQueries({ queryKey: channelKeys.detail(id) });
    },
  });
}

export function useDeleteChannel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => ChannelsService.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: channelKeys.all });
    },
  });
}

export function useCheckChannelStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => ChannelsService.status(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: channelKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: channelKeys.list(undefined) });
    },
  });
}

export function useTestChannel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => ChannelsService.test(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: channelKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: channelKeys.list(undefined) });
    },
  });
}

export function useAddChannelMix() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, content_id, position }: { id: string; content_id: string; position?: number }) =>
      ChannelsService.addMix(id, content_id, position),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: channelKeys.mixes(id) });
      queryClient.invalidateQueries({ queryKey: channelKeys.detail(id) });
    },
  });
}

export function useMoveChannelMix() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, mixId, position }: { id: string; mixId: string; position: number }) =>
      ChannelsService.moveMix(id, mixId, position),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: channelKeys.mixes(id) });
    },
  });
}

export function useRemoveChannelMix() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, mixId }: { id: string; mixId: string }) =>
      ChannelsService.removeMix(id, mixId),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: channelKeys.mixes(id) });
      queryClient.invalidateQueries({ queryKey: channelKeys.detail(id) });
    },
  });
}
